/**
 * Share Token client (Phase 6, section 3) - link-based project sharing.
 *
 * Talks to Nuvra's own server routes (src/server/routes/shareTokens.ts) with the
 * Supabase bearer token. Owners mint / list / revoke tokens; any signed-in user
 * redeems a link to become a project member at the token's role. The raw token is
 * returned once at creation and never re-shown; lists carry only a masked preview.
 */

import { authHeader } from "@/api/authToken";

// __API_URL__ baked by Vite from VITE_API_URL at build time.
declare const __API_URL__: string | undefined;
const API_BASE =
  (typeof __API_URL__ !== "undefined" ? __API_URL__ : "")?.replace(/\/$/, "") || "";

export interface ShareTokenView {
  id:            string;
  role:          string;
  tokenPreview:  string;
  expiresAt:     string | null;
  revoked:       boolean;
  redeemedCount: number;
  createdAt:     string | null;
}

export interface CreatedShareToken extends ShareTokenView {
  /** Raw token, returned once. Build the share link from it client-side. */
  token: string;
}

export interface RedeemOutcome {
  ok: boolean;
  projectId?: string;
  role?: string;
  error?: string;
}

async function authed(path: string, init?: RequestInit): Promise<Response> {
  const headers = { "Content-Type": "application/json", ...(await authHeader()), ...(init?.headers ?? {}) };
  return fetch(`${API_BASE}/api${path}`, { ...init, headers });
}

/** Build the user-facing share link for a raw token. */
export function shareLinkFor(rawToken: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/join?token=${encodeURIComponent(rawToken)}`;
}

export async function mintShareToken(
  projectId: string,
  role: "viewer" | "editor",
  expiresInDays?: number,
): Promise<CreatedShareToken | null> {
  try {
    const res = await authed(`/project/${encodeURIComponent(projectId)}/share-tokens`, {
      method: "POST",
      body:   JSON.stringify({ role, expiresInDays }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.shareToken ?? null;
  } catch {
    return null;
  }
}

export async function listShareTokens(projectId: string): Promise<ShareTokenView[]> {
  try {
    const res = await authed(`/project/${encodeURIComponent(projectId)}/share-tokens`);
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.shareTokens) ? json.shareTokens : [];
  } catch {
    return [];
  }
}

export async function revokeShareToken(projectId: string, tokenId: string): Promise<boolean> {
  try {
    const res = await authed(`/project/${encodeURIComponent(projectId)}/share-tokens/${encodeURIComponent(tokenId)}`, {
      method: "DELETE",
    });
    if (!res.ok) return false;
    const json = await res.json().catch(() => ({}));
    return json?.success === true;
  } catch {
    return false;
  }
}

export async function redeemShareLink(rawToken: string): Promise<RedeemOutcome> {
  try {
    const res = await authed(`/share/redeem`, {
      method: "POST",
      body:   JSON.stringify({ token: rawToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json?.error ?? "This link is invalid or has expired" };
    return { ok: true, projectId: json?.projectId, role: json?.role };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export const shareTokenClient = {
  shareLinkFor, mintShareToken, listShareTokens, revokeShareToken, redeemShareLink,
};
